import { type PointerEvent, useCallback, useEffect, useRef, useState } from "react";

export interface HoverIntentOptions {
  /**
   * Kursor shuncha ms to'xtab tursa ochiladi: panel ustidan pastga yurib
   * ketayotgan kursor yo'lidagi har bir menyuni ochib-yopib o'tmasin.
   */
  openDelay?: number;
  /**
   * Kursor chiqqach shuncha ms kutiladi: tugma bilan ochilgan panel orasida
   * bo'shliq bor va uni kesib o'tayotgan kursor menyuni yopib qo'ymasin.
   */
  closeDelay?: number;
}

/**
 * Oxirgi o'zgarishni nima qildi: kursor (`hover`) yoki to'g'ridan-to'g'ri
 * chaqiruv (`manual` — klaviatura, bosish, Escape, band tanlash). Fokus
 * qoidalari shunga qarab ajraladi: kursor bilan ochilgan menyu fokusni
 * o'ziga tortmaydi.
 */
export type HoverIntentSource = "hover" | "manual";

interface HoverIntentState {
  open: boolean;
  source: HoverIntentSource;
}

/**
 * Kursor bilan ochiladigan menyu holati.
 *
 * Faqat sichqoncha: sensorli ekranda "ustiga kelish" yo'q — barmoq bosishi
 * `pointerenter` ni ham chiqaradi va menyu bir vaqtning o'zida ham kursordan,
 * ham bosishdan ochilib-yopilib qolardi. Bosish esa menyu kutubxonasining
 * o'zida qoladi.
 */
export function useHoverIntent({ openDelay = 80, closeDelay = 180 }: HoverIntentOptions = {}) {
  const [state, setState] = useState<HoverIntentState>({ open: false, source: "manual" });
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const cancel = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = undefined;
  }, []);

  // Komponent yo'qolgach kechikkan taymer holatni o'zgartirmasin.
  useEffect(() => cancel, [cancel]);

  const schedule = useCallback(
    (open: boolean, delay: number) => {
      cancel();
      timer.current = setTimeout(() => {
        timer.current = undefined;
        // Holat o'zgarmasa manba ham o'zgarmaydi: klaviatura bilan ochilgan
        // menyuga kursor kirib qolsa, u "kursor ochgan" menyuga aylanmasin.
        setState(current => (current.open === open ? current : { open, source: "hover" }));
      }, delay);
    },
    [cancel],
  );

  const setOpen = useCallback(
    (open: boolean) => {
      cancel();
      setState({ open, source: "manual" });
    },
    [cancel],
  );

  const onPointerEnter = useCallback(
    (event: PointerEvent) => {
      if (event.pointerType === "mouse") schedule(true, openDelay);
    },
    [schedule, openDelay],
  );

  const onPointerLeave = useCallback(
    (event: PointerEvent) => {
      if (event.pointerType === "mouse") schedule(false, closeDelay);
    },
    [schedule, closeDelay],
  );

  return {
    open: state.open,
    source: state.source,
    setOpen,
    hoverProps: { onPointerEnter, onPointerLeave },
  };
}
